import FreeSvgUrl from "@icons/free.svg?url";
import SubscriptionSvgUrl from "@icons/subscription.svg?url";
import { Trans } from "@lingui/react/macro";
import { queryOptions, useSuspenseQuery } from "@tanstack/react-query";

import { useCompany } from "@/common/providers/company-provider";
import { useSidebar } from "@/common/providers/sidebar-provider/sidebar-provider";
import { invariant } from "@/common/utils/invariant";
import { getLocalizedPeriods } from "@/common/utils/period";
import { getLocalizedSubscriptionStatus } from "@/common/utils/subscription-status";
import {
  getCompany,
  PlanPeriodToType,
  PlanToType,
  SubscriptionToStatus,
} from "@/generated/api/users";
import { FullscreenModalLayout } from "@/routes/layout/layout.fullscreen";

import { Payments } from "./payments";

const Overview = () => {
  const subscriptions = useSubscriptions();

  return (
    <>
      <FullscreenModalLayout.Title>
        <Trans>Předplatné</Trans>
      </FullscreenModalLayout.Title>
      <div className="flex flex-col gap-y-4">
        {!subscriptions ? (
          <Subscription
            id={null}
            period={null}
            plan={{ description: description.BASIC, type: "BASIC" }}
            status={null}
          />
        ) : (
          <>
            {subscriptions.paid.map((s, i) => (
              <Subscription key={"subscription" + i.toString()} {...s} />
            ))}
            {subscriptions.trial.map((s, i) => (
              <TrialSubscription
                key={"trial-subscription" + i.toString()}
                {...s}
              />
            ))}
            <Payments />
          </>
        )}
      </div>
    </>
  );
};

const Subscription = ({
  id,
  period,
  plan,
  status,
}: {
  id: number | null;
  period: string | null;
  plan: { type: string; description: React.ReactNode };
  status: SubscriptionToStatus | null;
}) => {
  const sidebar = useSidebar();

  return (
    <button
      className="flex gap-4 rounded-2xl p-4 shadow-can-light-box"
      onClick={() =>
        sidebar.actions.openSubscription({
          subscriptionId: id,
        })
      }
    >
      <span className="flex h-14 w-14 shrink-0 items-center justify-center rounded-full bg-can-silver-gray">
        <img className="h-6 w-6" src={SubscriptionSvgUrl} />
      </span>
      <span className="text-left">
        <strong className="text-can-midnight-steel">
          {plan.type}
          {period ? " - " + period : null}
        </strong>
        <p className="text-xs">{plan.description}</p>
      </span>
      {status ? <Status status={status} /> : null}
    </button>
  );
};

const Status = ({ status }: { status: SubscriptionToStatus }) => {
  let item: { gradient: string; text: React.ReactNode } = {
    text: getLocalizedSubscriptionStatus()[status],
  } as typeof item;

  switch (status) {
    case "EXPIRED":
    case "PAST_DUE":
    case "INCOMPLETE": {
      item = {
        ...item,
        // eslint-disable-next-line lingui/no-unlocalized-strings
        gradient: "from-[#F99B87] to-[#E55743]",
      };
      break;
    }
    case "AWAITING_PAYMENT": {
      item = {
        ...item,
        // eslint-disable-next-line lingui/no-unlocalized-strings
        gradient: "from-can-tranquil-azure to-can-mystic-aqua",
      };
      break;
    }
    case "ACTIVE":
    case "DELETED": {
      item = {
        ...item,
        // eslint-disable-next-line lingui/no-unlocalized-strings
        gradient: "from-[#84D4A3] to-[#51B678]",
      };
      break;
    }
    case "CANCELLED": {
      item = {
        ...item,
        // eslint-disable-next-line lingui/no-unlocalized-strings
        gradient: "from-[#FFC684] to-[#FF914D]",
      };
      break;
    }
    default: {
      const exhaustiveCheck: never = status;
      throw new Error(`Unhandled status case: ${exhaustiveCheck}`);
    }
  }

  return (
    <small
      className={`ml-auto rounded-sm self-baseline bg-linear-to-r ${item.gradient} p-1.5 text-right text-xs font-normal text-white`}
    >
      {item.text}
    </small>
  );
};
const TrialSubscription = ({ id }: { id: number }) => {
  const sidebar = useSidebar();

  return (
    <div
      className="flex gap-4 rounded-2xl p-4 shadow-can-light-box"
      onClick={() => sidebar.actions.openSubscription({ subscriptionId: id })}
      role="button"
    >
      <span className="flex h-14 w-14 shrink-0 items-center justify-center rounded-full bg-can-silver-gray">
        <img className="h-6 w-6" src={FreeSvgUrl} />
      </span>
      <div>
        <strong className="text-can-midnight-steel">
          <Trans>Zkušební předplatné zdarma</Trans>
        </strong>
        <p className="text-xs">
          <Trans>
            Užijte si na omezenou dobu rozšířenou verzi aplikace zcela zdarma.
          </Trans>
        </p>
      </div>
    </div>
  );
};

const useSubscriptions = () => {
  const { company: activeCompany } = useCompany();
  invariant(activeCompany);
  const company = useSuspenseQuery(
    companyQuery({ companyId: activeCompany.id }),
  );
  const trialSubscriptions = company.data.subscriptions.filter(
    (s) => s.source === "TRIAL",
  );
  const paidSubscriptions = company.data.subscriptions.filter(
    (s) => s.source === "PAID" || s.source === "MANUAL",
  );

  const mapSubscription = (s: {
    id: number;
    period?: {
      type: PlanPeriodToType;
    };
    status: SubscriptionToStatus;
    plan: { type: PlanToType };
  }) => ({
    id: s.id,
    period: s.period
      ? getLocalizedPeriods()[s.period.type].toLowerCase()
      : null,
    plan: {
      description: description[s.plan.type],
      type: s.plan.type,
    },
    status: s.status,
  });

  if (trialSubscriptions.length === 0 && paidSubscriptions.length === 0) {
    // BASIC plan doesn't have any subscription by default
    return;
  }

  return {
    paid: paidSubscriptions.map(mapSubscription),
    trial: trialSubscriptions.map(mapSubscription),
  };
};

const companyQuery = ({ companyId }: { companyId: number }) => {
  return queryOptions({
    queryFn: ({ signal }) => getCompany(companyId, { signal }),
    queryKey: ["company", companyId],
  });
};

const description: Record<PlanToType, React.ReactNode> = {
  BASIC: (
    <Trans>
      Některé funkce jsou pro vás nedostupné. Odemkněte si všechny funkce ještě
      dnes!
    </Trans>
  ),
  PLUS: (
    <Trans>
      Některé funkce jsou pro vás nedostupné. Odemkněte si všechny funkce ještě
      dnes!
    </Trans>
  ),
  PREMIUM: (
    <Trans>
      Skvěle! Máte k dispozici všechny funkce, které vám v Clever˚Analytics'
      nabízíme.
    </Trans>
  ),
  TRADER: (
    <Trans>
      Některé funkce jsou pro vás nedostupné. Odemkněte si všechny funkce ještě
      dnes!
    </Trans>
  ),
};

export { Overview };
